import { ExternalLink, Github, Loader2 } from 'lucide-react';
import { type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { type AiTicket } from '../../services/api/tickets';
import { getApiErrorDetails } from '../../utils/api-error';
import { formatDateTime } from '../../utils/datetime';

interface TicketDetailProps {
  ticket: AiTicket | null;
  isLoading: boolean;
  error: unknown;
  onOpenSession?: (sessionId: string) => void;
  actions: ReactNode;
}

export const TicketDetail = ({
  ticket,
  isLoading,
  error,
  onOpenSession,
  actions,
}: TicketDetailProps) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Laddar ticket…
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-sm text-red-700">
        Kunde inte ladda ticketen{error ? `: ${getApiErrorDetails(error)}` : '.'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <p className="text-sm text-slate-700 max-w-3xl">{ticket.summary}</p>

          <div className="flex flex-wrap gap-1.5">
            {ticket.sessionIds.map((sessionId) => (
              <button
                key={sessionId}
                type="button"
                onClick={() => onOpenSession?.(sessionId)}
                title="Öppna sessionen"
                className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 font-mono text-[11px] text-indigo-700 hover:bg-indigo-100 transition"
              >
                {sessionId.slice(0, 8)}… <ExternalLink className="h-3 w-3" />
              </button>
            ))}
            {ticket.categories.map((category) => (
              <span
                key={category}
                className="inline-block rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] text-slate-600"
              >
                {category}
              </span>
            ))}
          </div>

          {ticket.manualNote && (
            <p className="text-xs text-slate-500">Notis vid skapande: ”{ticket.manualNote}”</p>
          )}

          {ticket.status === 'rejected' && (
            <p className="text-xs text-slate-500">
              Avvisad {formatDateTime(ticket.rejectedAt)}
              {ticket.rejectedReason ? ` — ${ticket.rejectedReason}` : ''}
            </p>
          )}

          {ticket.status === 'issue_created' && ticket.githubIssueUrl && (
            <a
              href={ticket.githubIssueUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:underline"
            >
              <Github className="h-4 w-4" /> {ticket.githubIssueUrl}
            </a>
          )}
        </div>

        {ticket.status === 'draft' && actions}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="prose prose-sm max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{ticket.bodyMarkdown}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
};
